/**
 * BarChart - Wiederverwendbare horizontale Balkendiagramm Komponente
 * 
 * Verwendet recharts für die Darstellung
 * 
 * Features:
 * - Echte Textbreitenmessung via Canvas measureText
 * - Dynamische Label-Breite bis max 45% der Container-Breite
 * - Umbrechen nur wenn Label nicht in verfügbare Breite passt
 * - Ellipsis nur als absoluter Notfall
 * - Responsive: funktioniert auf 13-27" Screens
 */

import { useMemo, useRef, useState, useEffect } from 'react';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

const CHART_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
];

const SONSTIGE_COLOR = '#9ca3af'; // gray-400 - dezenter Farbton für "Sonstige"
const SONSTIGE_ID = '__sonstige__';

// Layout-Konstanten
const TICK_FONT = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const MIN_LABEL_WIDTH = 60;
const MAX_LABEL_WIDTH_PERCENT = 0.45; // Max 45% der Container-Breite
const LABEL_PADDING = 12; // Padding rechts vom Label
const BAR_HEIGHT_SINGLE = 32;
const BAR_HEIGHT_MULTI = 44;
const LINE_HEIGHT = 13;

// Canvas Context für Textmessung (singleton, lazy init)
let measureCanvas: CanvasRenderingContext2D | null = null;

function getMeasureContext(): CanvasRenderingContext2D {
  if (!measureCanvas) {
    const canvas = document.createElement('canvas');
    measureCanvas = canvas.getContext('2d');
    if (measureCanvas) {
      measureCanvas.font = TICK_FONT;
    }
  }
  return measureCanvas!;
}

/**
 * Misst die tatsächliche Textbreite in Pixeln
 */
function measureTextWidth(text: string): number {
  const ctx = getMeasureContext();
  if (!ctx) return text.length * 6.5; // Fallback
  return ctx.measureText(text).width;
}

/**
 * Berechnet die benötigte Breite für alle Labels
 * Gibt die Breite des längsten Labels zurück
 */
function calculateRequiredWidth(labels: string[]): number {
  if (labels.length === 0) return MIN_LABEL_WIDTH;
  
  let maxWidth = 0;
  for (const label of labels) {
    const width = measureTextWidth(label);
    if (width > maxWidth) {
      maxWidth = width;
    }
  }
  
  return maxWidth + LABEL_PADDING;
}

/**
 * Bricht Text um, wenn er nicht in die verfügbare Breite passt
 * Versucht an Wortgrenzen zu brechen, Ellipsis nur als Notfall
 */
function wrapTextToFit(text: string, maxWidth: number, maxLines: number = 2): string[] {
  const textWidth = measureTextWidth(text);
  
  // Passt einzeilig → kein Wrap nötig
  if (textWidth <= maxWidth) {
    return [text];
  }
  
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';
  
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = measureTextWidth(testLine);
    
    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      // Aktuelle Zeile ist voll
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Einzelnes Wort zu lang - hart abschneiden mit Ellipsis
        currentLine = word;
      }
      
      // Max Zeilen erreicht?
      if (lines.length >= maxLines) {
        break;
      }
    }
  }
  
  // Rest hinzufügen
  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine);
  } else if (currentLine && lines.length >= maxLines) {
    // Letzte Zeile mit Ellipsis wenn noch Text übrig
    const lastIdx = lines.length - 1;
    const combined = lines[lastIdx] + ' ' + currentLine;
    if (measureTextWidth(combined) > maxWidth) {
      // Kürzen mit Ellipsis
      let truncated = lines[lastIdx];
      while (measureTextWidth(truncated + '…') > maxWidth && truncated.length > 3) {
        truncated = truncated.slice(0, -1);
      }
      lines[lastIdx] = truncated + '…';
    } else {
      lines[lastIdx] = combined;
    }
  }
  
  return lines.length > 0 ? lines : [text.slice(0, 10) + '…'];
}

interface LabelInfo {
  original: string;
  lines: string[];
  needsWrap: boolean;
}

/**
 * Berechnet Label-Layout basierend auf verfügbarer Breite
 */
function calculateLabelLayout(labels: string[], availableWidth: number): {
  labelWidth: number;
  labelInfos: Map<string, LabelInfo>;
  hasMultiLine: boolean;
} {
  const requiredWidth = calculateRequiredWidth(labels);
  
  // Label-Breite: so viel wie nötig, aber max availableWidth
  const labelWidth = Math.min(
    Math.max(requiredWidth, MIN_LABEL_WIDTH),
    availableWidth
  );
  
  const effectiveWidth = labelWidth - LABEL_PADDING;
  const labelInfos = new Map<string, LabelInfo>();
  let hasMultiLine = false;
  
  for (const label of labels) {
    const textWidth = measureTextWidth(label);
    const needsWrap = textWidth > effectiveWidth;
    const lines = needsWrap ? wrapTextToFit(label, effectiveWidth, 2) : [label];
    
    if (lines.length > 1) hasMultiLine = true;
    
    labelInfos.set(label, { original: label, lines, needsWrap });
  }
  
  return { labelWidth, labelInfos, hasMultiLine };
}

interface BarChartData {
  name: string;
  fullName: string;
  value: number;
  id?: string;
}

interface BarChartProps {
  data: BarChartData[];
  title?: string;
  unit?: string;
  height?: number;
  color?: string;
  maxItems?: number;
  emptyMessage?: string;
  /** Wenn true, wird die Reihenfolge der Daten beibehalten (keine interne Sortierung) */
  preserveOrder?: boolean;
}

export function HorizontalBarChart({
  data,
  title,
  unit = 'kg',
  height: providedHeight,
  color = '#3b82f6',
  maxItems = 5,
  emptyMessage = 'Keine Daten im Zeitraum',
  preserveOrder = false,
}: BarChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(400); // Default
  
  // Container-Breite messen
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        if (width > 0) setContainerWidth(width);
      }
    };
    
    updateWidth();
    
    // ResizeObserver für responsive Updates
    const observer = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  // Sort descending and take top N (unless preserveOrder is set)
  const sortedData = preserveOrder 
    ? [...data] 
    : [...data].sort((a, b) => b.value - a.value);
  const displayData = sortedData.slice(0, maxItems);
  
  // Max verfügbare Breite für Labels (45% der Container-Breite minus Padding)
  const maxAvailableLabelWidth = useMemo(() => {
    const chartAreaWidth = containerWidth - 32; // p-4 padding
    return Math.floor(chartAreaWidth * MAX_LABEL_WIDTH_PERCENT);
  }, [containerWidth]);
  
  // Label-Layout berechnen
  const { labelWidth, labelInfos, hasMultiLine } = useMemo(() => {
    const names = displayData.map(d => d.name);
    return calculateLabelLayout(names, maxAvailableLabelWidth);
  }, [displayData, maxAvailableLabelWidth]);
  
  // Dynamische Chart-Höhe basierend auf Anzahl der Balken und Label-Größe
  // providedHeight ist nur ein Hint - wir verwenden das Maximum aus provided und calculated
  const chartHeight = useMemo(() => {
    const barHeight = hasMultiLine ? BAR_HEIGHT_MULTI : BAR_HEIGHT_SINGLE;
    const minHeight = 150;
    const calculatedHeight = displayData.length * barHeight + 40;
    const requiredHeight = Math.max(minHeight, calculatedHeight);
    // providedHeight nur verwenden wenn es GRÖSSER ist als required (nie kleiner)
    return providedHeight ? Math.max(providedHeight, requiredHeight) : requiredHeight;
  }, [displayData.length, hasMultiLine, providedHeight]);

  // Check if all values are 0 or no data
  const hasData = displayData.length > 0 && displayData.some(d => d.value > 0);

  if (!hasData) {
    return (
      <div ref={containerRef} className="bg-white rounded-lg border p-4">
        {title && <h4 className="text-sm font-medium text-gray-700 mb-3">{title}</h4>}
        <div className="flex items-center justify-center h-32 bg-slate-50 rounded-lg border border-dashed border-slate-200">
          <p className="text-sm text-slate-400">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const isSonstige = item.id === SONSTIGE_ID;
      
      return (
        <div className="bg-white px-3 py-2 shadow-lg rounded border border-gray-200 text-sm">
          <p className="font-medium text-gray-900">
            {isSonstige ? item.name : item.fullName}
          </p>
          <p className="text-gray-600">
            {item.value.toLocaleString('de-DE')} {unit}
          </p>
          {isSonstige && (
            <p className="text-gray-400 text-xs mt-1">
              Summe der restlichen Entitäten
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Custom label formatter for bar values
  const renderLabel = (props: any) => {
    const { x, y, width, height: barHeight, value } = props;
    return (
      <text
        x={x + width + 5}
        y={y + barHeight / 2}
        fill="#64748b"
        fontSize={11}
        dominantBaseline="middle"
      >
        {value.toLocaleString('de-DE')}
      </text>
    );
  };
  
  // Custom Y-Axis Tick mit intelligenter Wrap-Logik
  const CustomYAxisTick = ({ x, y, payload }: any) => {
    const info = labelInfos.get(payload.value);
    const lines = info?.lines || [payload.value];
    
    // Vertikale Zentrierung bei mehrzeiligen Labels
    const offsetY = lines.length > 1 ? -LINE_HEIGHT / 2 : 0;
    
    return (
      <g>
        {lines.map((line, idx) => (
          <text
            key={idx}
            x={x - 4}
            y={y + offsetY + idx * LINE_HEIGHT}
            textAnchor="end"
            fill="#475569"
            fontSize={11}
            dominantBaseline="middle"
          >
            {line}
          </text>
        ))}
      </g>
    );
  };

  return (
    <div ref={containerRef} className="bg-white rounded-lg border p-4">
      {title && <h4 className="text-sm font-medium text-gray-700 mb-3">{title}</h4>}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <RechartsBarChart
          data={displayData}
          layout="vertical"
          margin={{ top: 5, right: 50, left: 5, bottom: 5 }}
        >
          <XAxis 
            type="number" 
            tickFormatter={(v) => v.toLocaleString('de-DE')}
            tick={{ fontSize: 10 }}
          />
          <YAxis 
            type="category" 
            dataKey="name" 
            width={labelWidth}
            tick={<CustomYAxisTick />}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {displayData.map((item, index) => {
              const isSonstige = item.id === SONSTIGE_ID;
              const fillColor = isSonstige 
                ? SONSTIGE_COLOR 
                : CHART_COLORS[index % CHART_COLORS.length];
              // Stabiler Key: id falls vorhanden, sonst name (beides unique pro Datenpunkt)
              const stableKey = item.id || item.name || `cell-${index}`;
              return <Cell key={stableKey} fill={fillColor} />;
            })}
            <LabelList dataKey="value" content={renderLabel} />
          </Bar>
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface VerticalBarChartProps {
  data: BarChartData[];
  title?: string;
  unit?: string;
  height?: number;
  color?: string;
}

export function VerticalBarChart({
  data,
  title,
  unit = 'kg',
  height = 200,
  color = '#3b82f6',
}: VerticalBarChartProps) {
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-4">
        {title && <h4 className="text-sm font-medium text-gray-700 mb-3">{title}</h4>}
        <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
          Keine Daten
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white px-3 py-2 shadow-lg rounded border border-gray-200 text-sm">
          <p className="font-medium text-gray-900">{item.name}</p>
          <p className="text-gray-600">
            {item.value.toLocaleString('de-DE')} {unit}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg border p-4">
      {title && <h4 className="text-sm font-medium text-gray-700 mb-3">{title}</h4>}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 11 }}
            interval={0}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis tickFormatter={(v) => v.toLocaleString('de-DE')} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}
